/**
 * WelcomeTitlesPage — Admin page for managing welcome scenes and title copywriting pools
 *
 * Left panel: Scene list (default + custom)
 * Right panel: Selected scene detail with title pool editor
 */

import { useState, useEffect } from 'react';
import { Lock, Trash2, Plus, MessageSquareText, ChevronDown, ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/ui/Button';
import { welcomeApi } from '@/lib/api/welcome';
import type { SceneCreateRequest } from '@/lib/api/welcome';
import {
  useWelcomeScenes,
  useWelcomeTitles,
  useCreateScene,
  useUpdateScene,
  useDeleteScene,
  useCreateTitle,
  useUpdateTitle,
  useDeleteTitle,
  useGenerateTitles,
} from '@/features/welcome-titles/hooks/useWelcomeTitles';
import { ConditionEditor } from '@/features/welcome-titles/components/ConditionEditor';
import { ScenePreview } from '@/features/welcome-titles/components/ScenePreview';
import type { WelcomeScene, WelcomeTitle, SceneCondition, GeneratedTitle } from '@/features/welcome-titles/types';

// ============================================================================
// TitleCard — single title row with 4 text fields
// ============================================================================

interface TitleCardProps {
  title: WelcomeTitle;
  sceneId: string;
  onUpdated: () => void;
}

function TitleCard({ title, sceneId, onUpdated }: TitleCardProps) {
  const [fields, setFields] = useState({
    text_zh: title.text_zh,
    text_zh_hant: title.text_zh_hant,
    text_en: title.text_en,
    text_zh_noname: title.text_zh_noname,
    text_zh_hant_noname: title.text_zh_hant_noname,
    text_en_noname: title.text_en_noname,
  });
  const [isDirty, setIsDirty] = useState(false);

  const updateMutation = useUpdateTitle();
  const deleteMutation = useDeleteTitle();

  const handleFieldChange = (key: keyof typeof fields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleToggleEnabled = () => {
    updateMutation.mutate(
      { titleId: title.id, sceneId, data: { is_enabled: !title.is_enabled } },
      { onSuccess: onUpdated }
    );
  };

  const handleSave = () => {
    if (!isDirty) return;
    updateMutation.mutate(
      { titleId: title.id, sceneId, data: fields },
      {
        onSuccess: () => {
          setIsDirty(false);
          toast.success('文案已保存');
          onUpdated();
        },
      }
    );
  };

  const handleDelete = () => {
    if (!confirm('确认删除这条文案?')) return;
    deleteMutation.mutate({ titleId: title.id, sceneId }, { onSuccess: onUpdated });
  };

  const textAreaClass =
    'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none bg-gray-50 focus:bg-white transition-colors';

  return (
    <div
      className={`rounded-xl border p-4 space-y-3 transition-all ${
        title.is_enabled ? 'border-gray-200 bg-white' : 'border-dashed border-gray-200 bg-gray-50 opacity-60'
      }`}
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-mono">#{title.sort_order + 1}</span>
          <button
            type="button"
            onClick={handleToggleEnabled}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
              title.is_enabled ? 'bg-indigo-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform ${
                title.is_enabled ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-xs text-gray-500">{title.is_enabled ? '启用' : '禁用'}</span>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <Button size="sm" onClick={handleSave} loading={updateMutation.isPending}>
              保存
            </Button>
          )}
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Text fields grid */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">简体 (含名字)</label>
          <textarea
            rows={2}
            value={fields.text_zh}
            onChange={(e) => handleFieldChange('text_zh', e.target.value)}
            className={textAreaClass}
            placeholder="例：{name}，今天是个好日子"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">繁體 (含名字)</label>
          <textarea
            rows={2}
            value={fields.text_zh_hant}
            onChange={(e) => handleFieldChange('text_zh_hant', e.target.value)}
            className={textAreaClass}
            placeholder="例：{name}，今天是個好日子"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">English (with name)</label>
          <textarea
            rows={2}
            value={fields.text_en}
            onChange={(e) => handleFieldChange('text_en', e.target.value)}
            className={textAreaClass}
            placeholder="e.g. Hi {name}, have a great day!"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">简体 (无名字)</label>
          <textarea
            rows={2}
            value={fields.text_zh_noname}
            onChange={(e) => handleFieldChange('text_zh_noname', e.target.value)}
            className={textAreaClass}
            placeholder="例：今天是个好日子"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">繁體 (無名字)</label>
          <textarea
            rows={2}
            value={fields.text_zh_hant_noname}
            onChange={(e) => handleFieldChange('text_zh_hant_noname', e.target.value)}
            className={textAreaClass}
            placeholder="例：今天是個好日子"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">English (no name)</label>
          <textarea
            rows={2}
            value={fields.text_en_noname}
            onChange={(e) => handleFieldChange('text_en_noname', e.target.value)}
            className={textAreaClass}
            placeholder="e.g. Have a great day!"
          />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// AiGenerateTitlesModal — AI generation dialog
// ============================================================================

interface AiGenerateTitlesModalProps {
  isOpen: boolean;
  onClose: () => void;
  scene: WelcomeScene;
  onTitlesAdded: () => void;
}

function AiGenerateTitlesModal({ isOpen, onClose, scene, onTitlesAdded }: AiGenerateTitlesModalProps) {
  // Phase: 'compose' → show custom prompt input, compose on click
  //        'edit'    → show full system/user prompts, editable, generate on click
  //        'results' → show generated titles with checkboxes
  type Phase = 'compose' | 'edit' | 'results';
  const [phase, setPhase] = useState<Phase>('compose');
  const [customPrompt, setCustomPrompt] = useState('');
  const [aiCount, setAiCount] = useState(5);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [generatedTitles, setGeneratedTitles] = useState<GeneratedTitle[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  const generateMutation = useGenerateTitles();
  const createTitleMutation = useCreateTitle();

  // Step 1: Compose prompts via backend
  const handleCompose = async () => {
    setIsComposing(true);
    try {
      const result = await welcomeApi.composePrompt({
        scene_key: scene.scene_key,
        scene_name: scene.name,
        scene_description: scene.description ?? undefined,
        custom_prompt: customPrompt || undefined,
        count: aiCount,
      });
      setSystemPrompt(result.system_prompt);
      setUserPrompt(result.user_prompt);
      setPhase('edit');
    } catch (err: unknown) {
      toast.error(`组合 Prompt 失败: ${err instanceof Error ? err.message : '未知错误'}`);
    } finally {
      setIsComposing(false);
    }
  };

  // Step 2: Send edited prompts to LLM
  const handleGenerate = async () => {
    const result = await generateMutation.mutateAsync({
      system_prompt: systemPrompt,
      user_prompt: userPrompt,
      count: aiCount,
    });
    setGeneratedTitles(result.titles);
    setSelectedIndices(new Set(result.titles.map((_, i) => i)));
    setPhase('results');
  };

  const toggleIndex = (i: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleAddSelected = async () => {
    const toAdd = generatedTitles.filter((_, i) => selectedIndices.has(i));
    if (toAdd.length === 0) {
      toast.error('请先选择要添加的文案');
      return;
    }
    setIsAdding(true);
    try {
      for (const t of toAdd) {
        await createTitleMutation.mutateAsync({
          sceneId: scene.id,
          data: {
            text_zh: t.text_zh,
            text_zh_hant: t.text_zh_hant,
            text_en: t.text_en,
            text_zh_noname: t.text_zh_noname,
            text_zh_hant_noname: t.text_zh_hant_noname,
            text_en_noname: t.text_en_noname,
            is_enabled: true,
          },
        });
      }
      toast.success(`已添加 ${toAdd.length} 条文案`);
      onTitlesAdded();
      onClose();
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    setPhase('compose');
    setGeneratedTitles([]);
    setSelectedIndices(new Set());
    setCustomPrompt('');
    setSystemPrompt('');
    setUserPrompt('');
    onClose();
  };

  const textAreaClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none font-mono';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl" title="AI 生成文案">
      <div className="space-y-4">
        {/* Scene info */}
        <div className="text-sm text-gray-500">
          场景：<span className="font-medium text-gray-700">{scene.name}</span>
          <span className="text-gray-400 ml-2">({scene.scene_key})</span>
        </div>

        {/* Phase 1: Compose */}
        {phase === 'compose' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">自定义指令（可选，会追加到 Prompt 末尾）</label>
              <textarea
                rows={3}
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                className={textAreaClass}
                placeholder="例如：风格轻松活泼，适合年轻用户；多用反问句..."
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600 whitespace-nowrap">生成数量</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={aiCount}
                  onChange={(e) => setAiCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
                  className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <span className="text-xs text-gray-400">模型跟随系统默认 (prompt_rewrite)</span>
            </div>

            <Button onClick={handleCompose} loading={isComposing} className="w-full">
              {isComposing ? '组合中...' : '预览 Prompt →'}
            </Button>
          </>
        )}

        {/* Phase 2: Edit prompts */}
        {phase === 'edit' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">System Prompt（可编辑）</label>
              <textarea
                rows={10}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                className={textAreaClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">User Prompt（可编辑）</label>
              <textarea
                rows={5}
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                className={textAreaClass}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPhase('compose')} className="flex-1">
                ← 返回修改
              </Button>
              <Button
                onClick={handleGenerate}
                loading={generateMutation.isPending}
                className="flex-1"
              >
                {generateMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    生成中...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    发送生成
                  </span>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Phase 3: Results */}
        {phase === 'results' && generatedTitles.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">生成结果</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {generatedTitles.map((t, i) => (
                <label
                  key={i}
                  className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                    selectedIndices.has(i)
                      ? 'border-indigo-300 bg-indigo-50'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIndices.has(i)}
                    onChange={() => toggleIndex(i)}
                    className="mt-0.5 rounded accent-indigo-600"
                  />
                  <span className="text-sm text-gray-700 break-all">
                    <span className="text-gray-900">{t.text_zh.slice(0, 30)}{t.text_zh.length > 30 ? '…' : ''}</span>
                    <span className="text-gray-400 mx-1">|</span>
                    <span className="text-gray-500">{t.text_zh_noname.slice(0, 30)}{t.text_zh_noname.length > 30 ? '…' : ''}</span>
                  </span>
                </label>
              ))}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between pt-2">
              <Button
                onClick={handleAddSelected}
                loading={isAdding}
                disabled={selectedIndices.size === 0}
              >
                添加选中 ({selectedIndices.size})
              </Button>
              <Button variant="outline" onClick={() => setPhase('edit')}>
                ← 重新编辑 Prompt
              </Button>
              <Button variant="outline" onClick={handleClose}>
                取消
              </Button>
            </div>
          </div>
        )}

        {/* Footer when no results yet */}
        {generatedTitles.length === 0 && (
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleClose}>
              取消
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================================================
// SceneDetail — right panel
// ============================================================================

interface SceneDetailProps {
  scene: WelcomeScene;
  onSceneUpdated: () => void;
}

function SceneDetail({ scene, onSceneUpdated }: SceneDetailProps) {
  const { data: titles = [], refetch: refetchTitles } = useWelcomeTitles(scene.id);
  const updateScene = useUpdateScene();
  const createTitle = useCreateTitle();
  const [showAiModal, setShowAiModal] = useState(false);

  // Local editable state for scene fields
  const [name, setName] = useState(scene.name);
  const [priority, setPriority] = useState(scene.priority);
  const [isEnabled, setIsEnabled] = useState(scene.is_enabled);
  const [condition, setCondition] = useState<SceneCondition | null>(scene.condition);
  const [sceneKey, setSceneKey] = useState(scene.scene_key);
  const [description, setDescription] = useState(scene.description ?? '');
  const [isSaving, setIsSaving] = useState(false);

  // Reset when scene changes
  useEffect(() => {
    setName(scene.name);
    setPriority(scene.priority);
    setIsEnabled(scene.is_enabled);
    setCondition(scene.condition);
    setSceneKey(scene.scene_key);
    setDescription(scene.description ?? '');
  }, [scene.id]);

  const handleSaveScene = async () => {
    setIsSaving(true);
    try {
      await updateScene.mutateAsync({
        id: scene.id,
        data: {
          name,
          priority,
          is_enabled: isEnabled,
          condition: scene.is_default ? undefined : condition,
          scene_key: scene.is_default ? undefined : sceneKey,
          description: description || null,
        },
      });
      onSceneUpdated();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTitle = () => {
    createTitle.mutate(
      {
        sceneId: scene.id,
        data: {
          text_zh: '',
          text_zh_hant: '',
          text_en: '',
          text_zh_noname: '',
          text_zh_hant_noname: '',
          text_en_noname: '',
          is_enabled: true,
          sort_order: titles.length,
        },
      },
      { onSuccess: () => refetchTitles() }
    );
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Scene Meta */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            {scene.is_default && <Lock className="w-4 h-4 text-gray-400" />}
            场景设置
          </h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsEnabled(!isEnabled)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  isEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform ${
                    isEnabled ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-500">{isEnabled ? '启用' : '禁用'}</span>
            </div>
            <Button size="sm" onClick={handleSaveScene} loading={isSaving}>
              保存场景
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">场景名称</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder="场景名称"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">优先级</label>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
              className={inputClass}
              placeholder="数值越大越优先"
            />
          </div>
          {!scene.is_default && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">场景 Key</label>
              <input
                type="text"
                value={sceneKey}
                onChange={(e) => setSceneKey(e.target.value)}
                className={inputClass}
                placeholder="scene_key"
              />
            </div>
          )}
          <div className={scene.is_default ? 'col-span-2' : ''}>
            <label className="block text-xs font-medium text-gray-600 mb-1">描述 (可选)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputClass}
              placeholder="场景描述"
            />
          </div>
        </div>

        {/* Condition editor for custom scenes */}
        {!scene.is_default && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">触发条件</label>
            <ConditionEditor value={condition} onChange={setCondition} />
          </div>
        )}
      </div>

      {/* Title Pool */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            文案库
            <span className="ml-2 text-xs text-gray-400 font-normal">
              {titles.length} 条
            </span>
          </h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowAiModal(true)}>
              <Sparkles className="w-4 h-4 mr-1" />
              AI 生成
            </Button>
            <Button size="sm" variant="outline" onClick={handleAddTitle} loading={createTitle.isPending}>
              <Plus className="w-4 h-4 mr-1" />
              添加文案
            </Button>
          </div>
        </div>

        {titles.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <MessageSquareText className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">暂无文案，点击"添加文案"开始创建</p>
          </div>
        ) : (
          <div className="space-y-3">
            {titles.map((title) => (
              <TitleCard
                key={title.id}
                title={title}
                sceneId={scene.id}
                onUpdated={refetchTitles}
              />
            ))}
          </div>
        )}
      </div>

      {/* AI Generate Titles Modal */}
      <AiGenerateTitlesModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
        scene={scene}
        onTitlesAdded={refetchTitles}
      />
    </div>
  );
}

// ============================================================================
// Scene Presets
// ============================================================================

interface ScenePreset {
  scene_key: string;
  name: string;
  description: string;
  priority: number;
  condition: SceneCondition;
  derive_eve: boolean;
  derive_after: boolean;
  group: 'gregorian' | 'lunar' | 'solar_term' | 'promotion';
}

const SCENE_PRESETS: ScenePreset[] = [
  // 公历节日 (priority 35-39, between birthday and default holidays)
  { scene_key: 'womens_day', name: '三八妇女节', description: '每年3月8日', priority: 35, condition: { type: 'date', month: 3, day: 8, calendar: 'gregorian' }, derive_eve: true, derive_after: false, group: 'gregorian' },
  { scene_key: 'april_fools', name: '愚人节', description: '每年4月1日', priority: 36, condition: { type: 'date', month: 4, day: 1, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'gregorian' },
  { scene_key: 'labor_day', name: '劳动节', description: '每年5月1日', priority: 35, condition: { type: 'date', month: 5, day: 1, calendar: 'gregorian' }, derive_eve: true, derive_after: true, group: 'gregorian' },
  { scene_key: 'internet_valentine', name: '520', description: '每年5月20日', priority: 36, condition: { type: 'date', month: 5, day: 20, calendar: 'gregorian' }, derive_eve: true, derive_after: false, group: 'gregorian' },
  { scene_key: 'childrens_day', name: '儿童节', description: '每年6月1日', priority: 36, condition: { type: 'date', month: 6, day: 1, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'gregorian' },
  { scene_key: 'teachers_day', name: '教师节', description: '每年9月10日', priority: 36, condition: { type: 'date', month: 9, day: 10, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'gregorian' },
  { scene_key: 'halloween', name: '万圣节', description: '每年10月31日', priority: 36, condition: { type: 'date', month: 10, day: 31, calendar: 'gregorian' }, derive_eve: true, derive_after: false, group: 'gregorian' },
  { scene_key: 'singles_day', name: '双十一', description: '每年11月11日', priority: 35, condition: { type: 'date', month: 11, day: 11, calendar: 'gregorian' }, derive_eve: true, derive_after: false, group: 'gregorian' },
  { scene_key: 'double_twelve', name: '双十二', description: '每年12月12日', priority: 36, condition: { type: 'date', month: 12, day: 12, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'gregorian' },
  { scene_key: 'christmas_eve', name: '平安夜', description: '每年12月24日', priority: 36, condition: { type: 'date', month: 12, day: 24, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'gregorian' },

  // 农历节日 (not already in defaults)
  { scene_key: 'dragon_head', name: '龙抬头', description: '农历二月初二', priority: 43, condition: { type: 'date', month: 2, day: 2, calendar: 'lunar' }, derive_eve: false, derive_after: false, group: 'lunar' },
  { scene_key: 'zhongyuan', name: '中元节', description: '农历七月十五', priority: 43, condition: { type: 'date', month: 7, day: 15, calendar: 'lunar' }, derive_eve: false, derive_after: false, group: 'lunar' },
  { scene_key: 'chongyang', name: '重阳节', description: '农历九月初九', priority: 43, condition: { type: 'date', month: 9, day: 9, calendar: 'lunar' }, derive_eve: false, derive_after: false, group: 'lunar' },
  { scene_key: 'laba', name: '腊八节', description: '农历腊月初八', priority: 43, condition: { type: 'date', month: 12, day: 8, calendar: 'lunar' }, derive_eve: false, derive_after: false, group: 'lunar' },

  // 24节气 (approximate gregorian dates — operators should verify)
  { scene_key: 'lichun', name: '立春', description: '约2月3-5日', priority: 37, condition: { type: 'date', month: 2, day: 4, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'yushui', name: '雨水', description: '约2月18-20日', priority: 37, condition: { type: 'date', month: 2, day: 19, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'jingzhe', name: '惊蛰', description: '约3月5-7日', priority: 37, condition: { type: 'date', month: 3, day: 6, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'chunfen', name: '春分', description: '约3月20-22日', priority: 37, condition: { type: 'date', month: 3, day: 20, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'qingming', name: '清明', description: '约4月4-6日', priority: 37, condition: { type: 'date', month: 4, day: 5, calendar: 'gregorian' }, derive_eve: true, derive_after: false, group: 'solar_term' },
  { scene_key: 'guyu', name: '谷雨', description: '约4月19-21日', priority: 37, condition: { type: 'date', month: 4, day: 20, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'lixia', name: '立夏', description: '约5月5-7日', priority: 37, condition: { type: 'date', month: 5, day: 6, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'xiaoman', name: '小满', description: '约5月20-22日', priority: 37, condition: { type: 'date', month: 5, day: 21, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'mangzhong', name: '芒种', description: '约6月5-7日', priority: 37, condition: { type: 'date', month: 6, day: 6, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'xiazhi', name: '夏至', description: '约6月21-22日', priority: 37, condition: { type: 'date', month: 6, day: 21, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'xiaoshu', name: '小暑', description: '约7月6-8日', priority: 37, condition: { type: 'date', month: 7, day: 7, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'dashu', name: '大暑', description: '约7月22-24日', priority: 37, condition: { type: 'date', month: 7, day: 23, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'liqiu', name: '立秋', description: '约8月7-9日', priority: 37, condition: { type: 'date', month: 8, day: 7, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'chushu', name: '处暑', description: '约8月22-24日', priority: 37, condition: { type: 'date', month: 8, day: 23, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'bailu', name: '白露', description: '约9月7-9日', priority: 37, condition: { type: 'date', month: 9, day: 8, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'qiufen', name: '秋分', description: '约9月22-24日', priority: 37, condition: { type: 'date', month: 9, day: 23, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'hanlu', name: '寒露', description: '约10月8-9日', priority: 37, condition: { type: 'date', month: 10, day: 8, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'shuangjian', name: '霜降', description: '约10月23-24日', priority: 37, condition: { type: 'date', month: 10, day: 23, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'lidong', name: '立冬', description: '约11月7-8日', priority: 37, condition: { type: 'date', month: 11, day: 7, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'xiaoxue', name: '小雪', description: '约11月22-23日', priority: 37, condition: { type: 'date', month: 11, day: 22, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'daxue', name: '大雪', description: '约12月6-8日', priority: 37, condition: { type: 'date', month: 12, day: 7, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'dongzhi', name: '冬至', description: '约12月21-23日', priority: 37, condition: { type: 'date', month: 12, day: 22, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'xiaohan', name: '小寒', description: '约1月5-7日', priority: 37, condition: { type: 'date', month: 1, day: 6, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },
  { scene_key: 'dahan', name: '大寒', description: '约1月20-21日', priority: 37, condition: { type: 'date', month: 1, day: 20, calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'solar_term' },

  // 促销活动 (date_range type)
  { scene_key: 'promo_618', name: '618大促', description: '6月1日-18日', priority: 36, condition: { type: 'date_range', start: [6, 1], end: [6, 18], calendar: 'gregorian' }, derive_eve: true, derive_after: true, group: 'promotion' },
  { scene_key: 'promo_1111', name: '双十一活动', description: '11月1日-11日', priority: 36, condition: { type: 'date_range', start: [11, 1], end: [11, 11], calendar: 'gregorian' }, derive_eve: true, derive_after: true, group: 'promotion' },
  { scene_key: 'promo_new_year', name: '年货节', description: '腊月至除夕期间', priority: 36, condition: { type: 'date_range', start: [1, 15], end: [2, 10], calendar: 'gregorian' }, derive_eve: false, derive_after: false, group: 'promotion' },
];

const PRESET_GROUPS = [
  { key: 'gregorian', label: '公历节日' },
  { key: 'lunar', label: '农历节日' },
  { key: 'solar_term', label: '24节气' },
  { key: 'promotion', label: '促销活动' },
] as const;

// ============================================================================
// CreateSceneModal
// ============================================================================

interface CreateSceneModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CreateSceneModal({ isOpen, onClose }: CreateSceneModalProps) {
  const createScene = useCreateScene();
  const [form, setForm] = useState<SceneCreateRequest>({
    scene_key: '',
    name: '',
    priority: 10,
    condition: null,
    description: '',
    derive_eve: false,
    derive_after: false,
  });

  const handleSubmit = () => {
    if (!form.scene_key.trim() || !form.name.trim()) {
      toast.error('场景 Key 和名称不能为空');
      return;
    }
    createScene.mutate(
      {
        ...form,
        description: form.description || null,
      },
      {
        onSuccess: () => {
          onClose();
          setForm({
            scene_key: '',
            name: '',
            priority: 10,
            condition: null,
            description: '',
            derive_eve: false,
            derive_after: false,
          });
        },
      }
    );
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <div className="p-6 space-y-5">
        <h2 className="text-lg font-semibold text-gray-900">新增自定义场景</h2>

        {/* Quick-select presets */}
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-600">快捷选择</label>
          {PRESET_GROUPS.map(group => {
            const presets = SCENE_PRESETS.filter(p => p.group === group.key);
            return (
              <details key={group.key} className="group">
                <summary className="cursor-pointer text-sm text-gray-700 hover:text-indigo-600 select-none py-1">
                  {group.label} ({presets.length})
                </summary>
                <div className="flex flex-wrap gap-1.5 mt-1 ml-5 pb-2">
                  {presets.map(preset => (
                    <button
                      key={preset.scene_key}
                      type="button"
                      onClick={() => setForm({
                        scene_key: preset.scene_key,
                        name: preset.name,
                        description: preset.description,
                        priority: preset.priority,
                        condition: preset.condition,
                        derive_eve: preset.derive_eve,
                        derive_after: preset.derive_after,
                      })}
                      className="px-2 py-0.5 text-xs rounded-full border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </details>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">场景 Key *</label>
            <input
              type="text"
              value={form.scene_key}
              onChange={(e) => setForm((p) => ({ ...p, scene_key: e.target.value }))}
              className={inputClass}
              placeholder="如 spring_festival"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">场景名称 *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className={inputClass}
              placeholder="如 春节"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">优先级</label>
            <input
              type="number"
              value={form.priority}
              onChange={(e) => setForm((p) => ({ ...p, priority: parseInt(e.target.value) || 0 }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">描述 (可选)</label>
            <input
              type="text"
              value={form.description ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">触发条件</label>
          <ConditionEditor
            value={form.condition ?? null}
            onChange={(c) => setForm((p) => ({ ...p, condition: c }))}
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.derive_eve}
              onChange={(e) => setForm((p) => ({ ...p, derive_eve: e.target.checked }))}
              className="rounded"
            />
            自动生成前夕场景
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.derive_after}
              onChange={(e) => setForm((p) => ({ ...p, derive_after: e.target.checked }))}
              className="rounded"
            />
            自动生成后续场景
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button onClick={handleSubmit} loading={createScene.isPending}>
            创建场景
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================================
// WelcomeTitlesPage — Main page
// ============================================================================

export function WelcomeTitlesPage() {
  const { data: scenes = [], isLoading, refetch } = useWelcomeScenes();
  const deleteScene = useDeleteScene();
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Auto-select first scene
  useEffect(() => {
    if (scenes.length > 0 && !selectedSceneId) {
      setSelectedSceneId(scenes[0].id);
    }
  }, [scenes, selectedSceneId]);

  const selectedScene = scenes.find((s) => s.id === selectedSceneId) ?? null;

  const defaultScenes = scenes.filter((s) => s.is_default);
  const customScenes = scenes.filter((s) => !s.is_default);

  const handleDeleteScene = (scene: WelcomeScene, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm(`确认删除场景 "${scene.name}"？此操作不可恢复。`)) return;
    deleteScene.mutate(scene.id, {
      onSuccess: () => {
        if (selectedSceneId === scene.id) {
          setSelectedSceneId(scenes.find((s) => s.id !== scene.id)?.id ?? null);
        }
      },
    });
  };

  const handleToggleSceneEnabled = async (scene: WelcomeScene, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await welcomeApi.updateScene(scene.id, { is_enabled: !scene.is_enabled });
      refetch();
    } catch {
      toast.error('更新失败');
    }
  };

  const SceneRow = ({ scene }: { scene: WelcomeScene }) => {
    const isSelected = selectedSceneId === scene.id;
    return (
      <div
        onClick={() => setSelectedSceneId(scene.id)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors group ${
          isSelected ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-700'
        }`}
      >
        <span className="flex-1 text-sm font-medium truncate">{scene.name}</span>
        <span
          className={`text-xs px-1.5 py-0.5 rounded font-mono ${
            isSelected ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {scene.priority}
        </span>

        {/* Enabled toggle */}
        <button
          type="button"
          onClick={(e) => handleToggleSceneEnabled(scene, e)}
          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors flex-shrink-0 ${
            scene.is_enabled ? 'bg-indigo-500' : 'bg-gray-300'
          }`}
          title={scene.is_enabled ? '点击禁用' : '点击启用'}
        >
          <span
            className={`inline-block h-2.5 w-2.5 rounded-full bg-white shadow transform transition-transform ${
              scene.is_enabled ? 'translate-x-3.5' : 'translate-x-0.5'
            }`}
          />
        </button>

        {/* Lock or delete */}
        {scene.is_default ? (
          <Lock className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
        ) : (
          <button
            type="button"
            onClick={(e) => handleDeleteScene(scene, e)}
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-gray-400 hover:text-red-500 transition-all flex-shrink-0"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  const SectionHeader = ({
    label,
    count,
    expanded,
    onToggle,
  }: {
    label: string;
    count: number;
    expanded: boolean;
    onToggle: () => void;
  }) => (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-1 px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors"
    >
      {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      {label}
      <span className="ml-auto font-mono normal-case">{count}</span>
    </button>
  );

  const [defaultExpanded, setDefaultExpanded] = useState(true);
  const [customExpanded, setCustomExpanded] = useState(true);

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <MessageSquareText className="w-5 h-5 text-indigo-600" />
              欢迎语管理
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              管理欢迎场景和各场景下的文案库
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            {showPreview ? '隐藏预览' : '场景预览'}
          </button>
        </div>

        {showPreview && (
          <div className="mt-4">
            <ScenePreview />
          </div>
        )}
      </div>

      {/* Body: two-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel: scene list */}
        <div className="w-72 flex-shrink-0 border-r border-gray-200 flex flex-col bg-white">
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {isLoading ? (
              <div className="text-center py-8 text-sm text-gray-400">加载中...</div>
            ) : (
              <>
                {/* Default scenes */}
                <SectionHeader
                  label="默认场景"
                  count={defaultScenes.length}
                  expanded={defaultExpanded}
                  onToggle={() => setDefaultExpanded((v) => !v)}
                />
                {defaultExpanded && defaultScenes.map((s) => <SceneRow key={s.id} scene={s} />)}

                {/* Custom scenes */}
                <div className="mt-2">
                  <SectionHeader
                    label="自定义场景"
                    count={customScenes.length}
                    expanded={customExpanded}
                    onToggle={() => setCustomExpanded((v) => !v)}
                  />
                  {customExpanded && customScenes.map((s) => <SceneRow key={s.id} scene={s} />)}
                </div>
              </>
            )}
          </div>

          {/* Add scene button */}
          <div className="p-3 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 text-sm hover:border-indigo-400 hover:text-indigo-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              新增场景
            </button>
          </div>
        </div>

        {/* Right panel: scene detail */}
        {selectedScene ? (
          <SceneDetail
            key={selectedScene.id}
            scene={selectedScene}
            onSceneUpdated={refetch}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageSquareText className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm">选择左侧场景开始编辑</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Scene Modal */}
      <CreateSceneModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
}
